// Pure data loading + preprocessing. No DOM access; callers pass the derived
// arrays into the graph and UI layers in main.ts.

import Papa from 'papaparse'
import {
  INIT_JITTER,
  POINT_SIZE_MIN,
  POINT_SIZE_RANGE,
  SPACE_CENTER,
} from './constants'

// ── CSV row shapes ─────────────────────────────────────────────────────────

export interface RawNode {
  Id: string
  Label: string
  Games: number
  Color: string
  Team: string
  x?: number | null
  y?: number | null
}
export interface RawEdge {
  Source: string
  Target: string
  GamesPlayed: number
  TeamsShared: string
}
interface RawTeam {
  TEAM: string
  LEAGUE: string
  CITY: string
  NICKNAME: string
  FIRST: number
  LAST: number
}

// ── League metadata ────────────────────────────────────────────────────────

const LEAGUE_NAMES: Record<string, string> = {
  AL: 'American League',
  NL: 'National League',
  AA: 'American Association',
  NA: 'National Association',
  FL: 'Federal League',
  PL: 'Players League',
  UA: 'Union Association',
  NNL: 'Negro National League',
  NAL: 'Negro American League',
  NSL: 'Negro Southern League',
  ECL: 'Eastern Colored League',
  ANL: 'American Negro League',
  EW: 'East–West League',
}

// NN1 (1920–31) and NN2 (1933–48) are the two distinct Negro National League
// eras; merged into NNL for display per user request.
function normalizeLeague(code: string): string {
  return code === 'NN1' || code === 'NN2' ? 'NNL' : code
}
export function leagueDisplayName(code: string): string {
  return LEAGUE_NAMES[code] ?? code
}

const LEAGUE_ORDER = [
  'AL', 'NL', 'AA', 'NA', 'FL', 'PL', 'UA',
  'NNL', 'NAL', 'NSL', 'ECL', 'ANL', 'EW',
]

// ── Team color palette ─────────────────────────────────────────────────────

export interface TeamPalette { primary: string; secondary: string; known: boolean }

// Ordered by authority: active MLB (well-documented), franchise predecessors
// (inferred from lineage), historical 19th-c. NL, Federal League (mostly
// unknown), Negro Leagues (mixed). known=false entries are best-guess
// placeholders — see plan doc for research notes.
export const TEAM_COLORS: Record<string, TeamPalette> = {
  // Active MLB
  ARI: { primary: '#A71930', secondary: '#E3D4AD', known: true },
  ATL: { primary: '#13274F', secondary: '#CE1141', known: true },
  BAL: { primary: '#000000', secondary: '#DF4601', known: true },
  BOS: { primary: '#BD3039', secondary: '#0C2340', known: true },
  CHA: { primary: '#27251F', secondary: '#C4CED4', known: true },
  CHN: { primary: '#0E3386', secondary: '#CC3433', known: true },
  CIN: { primary: '#C6011F', secondary: '#FFFFFF', known: true },
  CLE: { primary: '#0C2340', secondary: '#E50022', known: true },
  COL: { primary: '#333366', secondary: '#C4CED4', known: true },
  DET: { primary: '#0C2340', secondary: '#FA4616', known: true },
  HOU: { primary: '#002D62', secondary: '#EB6E1F', known: true },
  KCA: { primary: '#004687', secondary: '#BD9B60', known: true },
  LAN: { primary: '#005A9C', secondary: '#FFFFFF', known: true },
  MIA: { primary: '#00A3E0', secondary: '#FFFFFF', known: true },
  MIL: { primary: '#12284B', secondary: '#FFC52F', known: true },
  MIN: { primary: '#002B5C', secondary: '#D31145', known: true },
  NYA: { primary: '#0C2340', secondary: '#C4CED3', known: true },
  NYN: { primary: '#002D72', secondary: '#FF5910', known: true },
  OAK: { primary: '#003831', secondary: '#EFB21E', known: true },
  PHI: { primary: '#E81828', secondary: '#002D72', known: true },
  PIT: { primary: '#000000', secondary: '#FDB827', known: true },
  SDN: { primary: '#2F241D', secondary: '#FFC425', known: true },
  SEA: { primary: '#0C2C56', secondary: '#005C5C', known: true },
  SFN: { primary: '#FD5A1E', secondary: '#27251F', known: true },
  SLN: { primary: '#C41E3A', secondary: '#0C2340', known: true },
  TBA: { primary: '#092C5C', secondary: '#8FBCE6', known: true },
  TEX: { primary: '#003278', secondary: '#C0111F', known: true },
  TOR: { primary: '#134A8E', secondary: '#E8291C', known: true },
  WAS: { primary: '#14225A', secondary: '#AB0003', known: true },
  ATH: { primary: '#003831', secondary: '#EFB21E', known: true },
  ANA: { primary: '#BA0021', secondary: '#003263', known: true },
  LAA: { primary: '#BA0021', secondary: '#003263', known: true },
  CAL: { primary: '#BA0021', secondary: '#003263', known: true },

  // Franchise predecessors (inferred from franchise lineage)
  BRO: { primary: '#005A9C', secondary: '#FFFFFF', known: true },
  BSN: { primary: '#13274F', secondary: '#CE1141', known: true },
  MLN: { primary: '#13274F', secondary: '#CE1141', known: true },
  NY1: { primary: '#FD5A1E', secondary: '#27251F', known: true },
  PHA: { primary: '#003831', secondary: '#EFB21E', known: true },
  KC1: { primary: '#003831', secondary: '#EFB21E', known: true },
  SLA: { primary: '#8B4513', secondary: '#FF8C00', known: true },
  WS1: { primary: '#002B5C', secondary: '#D31145', known: true },
  WS2: { primary: '#003278', secondary: '#C0111F', known: true },
  MON: { primary: '#002F6C', secondary: '#EF3E42', known: true },
  FLO: { primary: '#000000', secondary: '#00A3E0', known: true },
  SE1: { primary: '#002F6C', secondary: '#FFC425', known: true },
  MLA: { primary: '#12284B', secondary: '#FFC52F', known: false },

  // 19th-c. NL
  BLN: { primary: '#000000', secondary: '#DF4601', known: false },
  CL4: { primary: '#0C2340', secondary: '#C4CED4', known: false },
  LS3: { primary: '#0C2340', secondary: '#C4CED4', known: false },
  WSN: { primary: '#0C2340', secondary: '#C4CED4', known: false },

  // Federal League (mostly unknown)
  BLF: { primary: '#005A31', secondary: '#E68231', known: false },
  BRF: { primary: '#1B365D', secondary: '#C4CED4', known: false },
  BUF: { primary: '#003087', secondary: '#C4CED4', known: false },
  CHF: { primary: '#0C2340', secondary: '#C4CED4', known: false },
  KCF: { primary: '#8B4513', secondary: '#C4CED4', known: false },
  NEW: { primary: '#8B0000', secondary: '#C4CED4', known: false },
  PTF: { primary: '#0C2340', secondary: '#C4CED4', known: false },
  SLF: { primary: '#0C2340', secondary: '#C4CED4', known: false },

  // Negro Leagues (mixed authority)
  KCM: { primary: '#003087', secondary: '#000000', known: true },
  CAG: { primary: '#000000', secondary: '#A71930', known: false },
  PTC: { primary: '#000000', secondary: '#BD3039', known: false },
  HOM: { primary: '#000000', secondary: '#7C7C7C', known: false },
  NW2: { primary: '#BD3039', secondary: '#FFFFFF', known: false },
  MEM: { primary: '#BD3039', secondary: '#FFFFFF', known: false },
  BIR: { primary: '#000000', secondary: '#8B4513', known: false },
  BLG: { primary: '#000000', secondary: '#BD9B60', known: false },
  CV9: { primary: '#3D3D3D', secondary: '#8B4513', known: false },
  CVB: { primary: '#8B4513', secondary: '#C4CED4', known: false },
  IN9: { primary: '#BD3039', secondary: '#FFC425', known: false },
  NY5: { primary: '#000000', secondary: '#C4CED4', known: false },
  NY6: { primary: '#BD3039', secondary: '#C4CED4', known: false },
  PH5: { primary: '#1B365D', secondary: '#C4CED4', known: false },
  PRG: { primary: '#000000', secondary: '#BD9B60', known: false },
  SNO: { primary: '#0C2340', secondary: '#FFC425', known: false },
  WEG: { primary: '#0C2340', secondary: '#BD9B60', known: false },
}

export const FALLBACK_PALETTE: TeamPalette = {
  primary: '#6B7280', secondary: '#D1D5DB', known: false,
}

export function teamPalette(team: string): TeamPalette {
  return TEAM_COLORS[team] ?? FALLBACK_PALETTE
}

// ── Fetch + parse helpers ──────────────────────────────────────────────────

async function fetchCsv<T>(url: string): Promise<T[]> {
  const res = await fetch(url)
  const text = await res.text()
  return Papa.parse<T>(text, { header: true, dynamicTyping: true, skipEmptyLines: true }).data
}

function formatName(raw: string): string {
  const [last, first] = raw.split(', ')
  return first ? `${first} ${last}` : raw
}

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.replace('#', ''), 16)
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface TeamMeta {
  fullName: string
  league: string
  first: number | null
  last: number | null
}

// Edge with numeric endpoints resolved once; avoids per-rebuild Map lookups.
export interface Edge {
  src: number
  tgt: number
  gamesPlayed: number
}

export interface LoadedData {
  rawNodes: RawNode[]
  nodeLabels: string[]
  pointSizes: Float32Array
  pointColors: Float32Array
  pointPositions: Float32Array
  edges: Edge[]
  maxGames: number
  minPlayerGames: number
  maxPlayerGames: number
  indicesByTeam: Map<string, number[]>
  allTeams: string[]
  sortedLeagues: string[]
  teamsByLeague: Map<string, string[]>
  fullTeamName: (team: string) => string
  teamYears: (team: string) => string
  playerPasses: (i: number, cutoff: number) => boolean
}

export async function loadData(): Promise<LoadedData> {
  const [rawNodes, rawEdges, rawTeams] = await Promise.all([
    fetchCsv<RawNode>('data/nodes.csv'),
    fetchCsv<RawEdge>('data/edges.csv'),
    fetchCsv<RawTeam>('data/teams.csv'),
  ])

  // Team metadata (last-row-wins for duplicate abbreviations in old data).
  const teamMeta = new Map<string, TeamMeta>()
  for (const row of rawTeams) {
    const abr = row.TEAM?.trim()
    if (!abr) continue
    const leagues = (row.LEAGUE ?? '').split(';').map(s => normalizeLeague(s.trim())).filter(Boolean)
    teamMeta.set(abr, {
      fullName: `${row.CITY} ${row.NICKNAME}`,
      league: leagues.at(-1) ?? '',
      first: typeof row.FIRST === 'number' ? row.FIRST : null,
      last: typeof row.LAST === 'number' ? row.LAST : null,
    })
  }

  const indexById = new Map(rawNodes.map((n, i) => [String(n.Id), i]))

  const nodeLabels = rawNodes.map(n => formatName(n.Label))

  // Point sizes scaled by sqrt(games) to dampen the heavy tail.
  const sqrtGames = rawNodes.map(n => Math.sqrt(Math.max(1, n.Games)))
  const maxSqrt = sqrtGames.reduce((m, v) => v > m ? v : m, 0)
  const pointSizes = new Float32Array(
    sqrtGames.map(s => POINT_SIZE_MIN + (s / maxSqrt) * POINT_SIZE_RANGE)
  )

  // RGBA colors sourced from TEAM_COLORS (primary). The underlying shape color
  // sits below the per-team image and shows through where the image is
  // transparent — matching the image fill keeps orphan-mask fades coherent.
  const pointColors = new Float32Array(rawNodes.length * 4)
  rawNodes.forEach((n, i) => {
    const [r, g, b] = hexToRgb(teamPalette(n.Team).primary)
    pointColors[i * 4]     = r / 255
    pointColors[i * 4 + 1] = g / 255
    pointColors[i * 4 + 2] = b / 255
    pointColors[i * 4 + 3] = 1
  })

  // Initial positions: baked (x, y) when present, else jitter around the
  // space center so pair-wise forces aren't collinear.
  const pointPositions = new Float32Array(rawNodes.length * 2)
  for (let i = 0; i < rawNodes.length; i++) {
    const n = rawNodes[i]!
    const hasBaked = typeof n.x === 'number' && Number.isFinite(n.x)
                   && typeof n.y === 'number' && Number.isFinite(n.y)
    if (hasBaked) {
      pointPositions[i * 2]     = n.x as number
      pointPositions[i * 2 + 1] = n.y as number
    } else {
      pointPositions[i * 2]     = SPACE_CENTER + (Math.random() - 0.5) * INIT_JITTER
      pointPositions[i * 2 + 1] = SPACE_CENTER + (Math.random() - 0.5) * INIT_JITTER
    }
  }

  // Precompute numeric edges once so filter rebuilds don't re-hash the
  // Source/Target strings.
  const edges: Edge[] = []
  for (const e of rawEdges) {
    const src = indexById.get(String(e.Source))
    const tgt = indexById.get(String(e.Target))
    if (src === undefined || tgt === undefined) continue
    edges.push({ src, tgt, gamesPlayed: e.GamesPlayed })
  }
  const maxGames = edges.reduce((m, e) => e.gamesPlayed > m ? e.gamesPlayed : m, 0)
  const maxPlayerGames = rawNodes.reduce((m, n) => Math.max(m, n.Games ?? 0), 0)
  const minPlayerGames = rawNodes.reduce(
    (m, n) => Math.min(m, n.Games ?? Infinity),
    Infinity,
  )

  // Group node indices by team; derive allTeams from the keys.
  const indicesByTeam = new Map<string, number[]>()
  rawNodes.forEach((n, i) => {
    if (!n.Team) return
    const list = indicesByTeam.get(n.Team) ?? []
    list.push(i)
    indicesByTeam.set(n.Team, list)
  })
  const allTeams = [...indicesByTeam.keys()]

  // Group teams by league; sort each bucket alphabetically by full name.
  const teamsByLeague = new Map<string, string[]>()
  for (const abr of allTeams) {
    const league = teamMeta.get(abr)?.league || 'Other'
    const list = teamsByLeague.get(league) ?? []
    list.push(abr)
    teamsByLeague.set(league, list)
  }
  for (const list of teamsByLeague.values()) {
    list.sort((a, b) => (teamMeta.get(a)?.fullName ?? a).localeCompare(teamMeta.get(b)?.fullName ?? b))
  }
  const sortedLeagues = [...teamsByLeague.keys()].sort((a, b) => {
    const ai = LEAGUE_ORDER.indexOf(a), bi = LEAGUE_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  const fullTeamName = (team: string) => teamMeta.get(team)?.fullName ?? team
  const teamYears = (team: string) => {
    const meta = teamMeta.get(team)
    if (!meta || meta.first == null || meta.last == null) return ''
    return meta.first === meta.last ? `${meta.first}` : `${meta.first}–${meta.last}`
  }
  const playerPasses = (i: number, cutoff: number) =>
    (rawNodes[i]?.Games ?? 0) >= cutoff

  return {
    rawNodes, nodeLabels, pointSizes, pointColors, pointPositions,
    edges, maxGames, minPlayerGames, maxPlayerGames,
    indicesByTeam, allTeams,
    sortedLeagues, teamsByLeague,
    fullTeamName, teamYears, playerPasses,
  }
}
